"use client";

import Link from "@/components/ui/LocaleLink";
import { useTranslations } from "next-intl";
import { MagnifyingGlass } from "@phosphor-icons/react/dist/ssr";
import NavActiveLink from "./NavActiveLink";

const LINK_CLASS =
  "group relative inline-flex items-center gap-[6px] whitespace-nowrap pb-3 text-sm font-medium uppercase tracking-wider text-navy transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold";

// Icon uses `translateY` (paint-only, no box-expansion) to nudge optical
// centering against the text. The earlier `marginTop: -2` looked similar
// but actually fed into the parent LI's line-box: an LI containing an
// inline-flex with mixed icon+text contents got a slightly smaller line
// box than an LI with text-only inline-flex contents (Find = 25.59px tall,
// Insights / How It Works = 26.50px). `items-center` on the UL then
// centered different-height LIs, leaving "Find a Professional" 1.55px
// higher than its peers — visible as a subtle baseline mismatch.
//
// The structural fix is below: each LI is `flex items-center` so it
// collapses to its child `<a>`'s exact height (23px). That makes all
// three LIs identical-size, and items-center aligns them precisely.
// Labels resolve via the `nav` namespace; `key` indexes into it.
const LINKS = [
  { href: "/search",       key: "findProfessional", icon: <MagnifyingGlass size={14} weight="bold" aria-hidden="true" style={{ transform: "translateY(-1px)" }} /> },
  { href: "/insights",     key: "insights"          },
];

export default function NavLinks() {
  const t = useTranslations("nav");
  return (
    <>
      {LINKS.map(({ href, key, icon }) => (
        <li key={href} className="flex items-center">
          <NavActiveLink href={href} label={t(key)} icon={icon} />
        </li>
      ))}
    </>
  );
}

/**
 * Active-state-free version of the nav links, used as the <Suspense> fallback
 * for <NavLinks>. NavActiveLink reads usePathname() (dynamic on [slug] routes
 * under cacheComponents); this fallback ships the links in the static shell —
 * with hover-only underlines — and the active state streams in via NavLinks.
 * On static routes the real NavLinks prerenders directly (pathname is known).
 */
export function NavLinksFallback() {
  const t = useTranslations("nav");
  return (
    <>
      {LINKS.map(({ href, key, icon }) => (
        <li key={href} className="flex items-center">
          <Link href={href} className={LINK_CLASS}>
            {icon}
            {t(key)}
            <span
              aria-hidden
              className="absolute bottom-0 left-0 h-[2px] w-0 bg-gold transition-all duration-200 group-hover:w-full"
            />
          </Link>
        </li>
      ))}
    </>
  );
}
